import { API_CONFIG, authUrl } from '@/config/api';
import { clearAccessToken, getAccessToken, setAccessToken } from '@/auth/tokenStore';

type ApiFetchOptions = RequestInit & {
  skipAuth?: boolean;
  /** Override `API_CONFIG.API_FETCH_TIMEOUT_MS` for this request. */
  timeoutMs?: number;
};

const REFRESH_PATH = authUrl('refresh');

let refreshPromise: Promise<boolean> | null = null;

function createTimeoutSignal(
  timeoutMs: number,
  outerSignal?: AbortSignal | null,
): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, timeoutMs);

  const onOuterAbort = () => {
    controller.abort(outerSignal?.reason);
  };
  if (outerSignal) {
    if (outerSignal.aborted) {
      controller.abort(outerSignal.reason);
    } else {
      outerSignal.addEventListener('abort', onOuterAbort);
    }
  }

  return {
    signal: controller.signal,
    dispose: () => {
      window.clearTimeout(timeoutId);
      outerSignal?.removeEventListener('abort', onOuterAbort);
    },
  };
}

async function fetchWithOptions(url: string, options?: ApiFetchOptions): Promise<Response> {
  const { skipAuth, timeoutMs, signal, ...init } = options ?? {};
  const effectiveTimeout = timeoutMs ?? API_CONFIG.API_FETCH_TIMEOUT_MS;
  const { signal: timeoutSignal, dispose } = createTimeoutSignal(effectiveTimeout, signal ?? undefined);

  try {
    const token = getAccessToken();
    const headers = new Headers(init.headers ?? {});
    if (!skipAuth && token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return await fetch(url, {
      ...init,
      headers,
      signal: timeoutSignal,
      credentials: init.credentials ?? 'include',
    });
  } finally {
    dispose();
  }
}

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(REFRESH_PATH, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        clearAccessToken();
        return false;
      }
      const payload = (await res.json()) as { access_token?: string };
      if (!payload.access_token) {
        clearAccessToken();
        return false;
      }
      setAccessToken(payload.access_token);
      return true;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function apiFetch(url: string, options?: ApiFetchOptions): Promise<Response> {
  const firstRes = await fetchWithOptions(url, options);

  if (options?.skipAuth || firstRes.status !== 401) {
    return firstRes;
  }

  const refreshed = await refreshAccessToken();
  if (!refreshed) return firstRes;

  const retryHeaders = new Headers(options?.headers ?? {});
  const newToken = getAccessToken();
  if (newToken) retryHeaders.set('Authorization', `Bearer ${newToken}`);
  return fetchWithOptions(url, {
    ...options,
    headers: retryHeaders,
  });
}
